import { apiClient } from "./client";

export type PermissionGroup = {
  id: string;
  label: string;
  permissions: string[];
};

type PermissionItem = {
  id?: string;
  _id?: string;
  label?: string;
  name?: string;
  group?: string;
  category?: string;
  module?: string;
  permissions?: string[];
  items?: string[];
  values?: string[];
  permission?: string;
  code?: string;
};

type PermissionPayload =
  | PermissionGroup[]
  | PermissionItem[]
  | string[]
  | Record<string, string[]>;

type PermissionsResponse = {
  data?: PermissionPayload;
  permissions?: PermissionPayload;
  items?: PermissionPayload;
  results?: PermissionPayload;
};

const toGroupId = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const toGroupLabel = (value: string) => {
  if (!value) return "Permissions";
  return value
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const isStringArray = (value: unknown[]): value is string[] =>
  value.every((item) => typeof item === "string");

const normalizePermissions = (payload: PermissionPayload): PermissionGroup[] => {
  if (Array.isArray(payload)) {
    if (payload.length === 0) return [];
    if (isStringArray(payload)) {
      return [
        {
          id: "all",
          label: "Permissions",
          permissions: payload as string[],
        },
      ];
    }

    const items = payload as PermissionItem[];
    const groupedByLabel = items.every((item) => Array.isArray(item.permissions));

    if (groupedByLabel) {
      return items.map((item) => {
        const label =
          item.label ??
          item.name ??
          item.group ??
          item.category ??
          item.module ??
          "Permissions";
        const permissions = item.permissions ?? item.items ?? item.values ?? [];
        return {
          id: item.id ?? item._id ?? toGroupId(label),
          label,
          permissions,
        };
      });
    }

    const bucket = new Map<string, string[]>();
    items.forEach((item) => {
      const label = item.group ?? item.category ?? item.module ?? "Permissions";
      const value = item.permission ?? item.code ?? item.name ?? "";
      if (!value) return;
      const list = bucket.get(label) ?? [];
      list.push(value);
      bucket.set(label, list);
    });

    return Array.from(bucket.entries()).map(([label, permissions]) => ({
      id: toGroupId(label),
      label,
      permissions,
    }));
  }

  if (payload && typeof payload === "object") {
    return Object.entries(payload).map(([label, permissions]) => ({
      id: toGroupId(label),
      label: toGroupLabel(label),
      permissions,
    }));
  }

  return [];
};

export const getPermissions = async () => {
  const response = await apiClient.get<PermissionsResponse | PermissionPayload>(
    "/v1/admin/permissions",
  );

  const body = response.data ?? [];
  const payload =
    (body as PermissionsResponse).data ??
    (body as PermissionsResponse).permissions ??
    (body as PermissionsResponse).items ??
    (body as PermissionsResponse).results ??
    body;

  return normalizePermissions(payload as PermissionPayload);
};
